import { range } from "range";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import getItemQuantityText from "~/ProductDetails/Pricing/AddToCart/getItemQuantityText";
import getMinBuyForQuantityDropdown from "~/ProductDetails/Pricing/AddToCart/getMinBuyForQuantityDropdown";

interface Props {
  quantity: string;
  minBuy: number;
  mustBuy: number;
  unitOfMeasure: string;
  handleQuantityChange?: (quantity: string) => void;
  setAddToCartDisabled: Dispatch<SetStateAction<boolean>>;
}

const VirtualGroupingQuantitySelection = ({
  quantity,
  minBuy,
  mustBuy,
  unitOfMeasure,
  handleQuantityChange,
  setAddToCartDisabled,
}: Props) => {
  const numberOfOptions = 15;
  const startQuantity = getMinBuyForQuantityDropdown({ minBuy, mustBuy });
  const highestOptionDisplayed = startQuantity + numberOfOptions * mustBuy;
  const itemQuantityText = getItemQuantityText(unitOfMeasure);
  const [showCustomQuantity, setShowCustomQuantity] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!quantity || Number(quantity) < startQuantity) {
      handleQuantityChange?.(startQuantity.toString());
    } else if (Number(quantity) > highestOptionDisplayed) {
      setShowCustomQuantity(true);
    }
  }, [handleQuantityChange, highestOptionDisplayed, quantity, startQuantity]);

  const handleOnChange = (
    event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    const inputValue = event.target.value;

    if (inputValue === "other") {
      setShowCustomQuantity(true);
      handleQuantityChange?.(startQuantity.toString());
      return;
    }

    const validationErrors: string[] = [];
    let errorsExist = false;

    const input = Number(inputValue);
    if (Number.isNaN(Number(input)) || input < startQuantity) {
      errorsExist = true;
      validationErrors.push(`Minimum of ${startQuantity} is required.`);
    }

    if (mustBuy > 1 && input % mustBuy !== 0) {
      errorsExist = true;
      validationErrors.push(
        `Please enter a quantity as a multiple of ${mustBuy}`,
      );
    }

    if (!errorsExist) {
      setErrors([]);
      handleQuantityChange?.(inputValue);
      setAddToCartDisabled(false);
    } else {
      setErrors(validationErrors);
      setAddToCartDisabled(true);
    }
  };

  return (
    <div>
      <div className="form__group">
        {minBuy > 1 && mustBuy === 1 && (
          <p className="small">
            You must buy a minimum of {minBuy} due to manufacturer packaging.
          </p>
        )}
        {minBuy > 1 && mustBuy > 1 && (
          <p className="small">
            You must buy a minimum of {minBuy} and in additional increments of{" "}
            {mustBuy} due to manufacturer packaging.
          </p>
        )}
        {minBuy === 1 && mustBuy > 1 && (
          <p className="small">
            You must buy a minimum of {mustBuy} at a time due to manufacturer
            packaging.
          </p>
        )}
        {!showCustomQuantity && (
          <div className="form__group">
            <div className="form__control-container">
              <select
                value={quantity}
                name="qty"
                className="form__control"
                onChange={handleOnChange}
                data-testid="quantity-dropdown"
              >
                <option disabled value="0">
                  {`Select ${itemQuantityText}`}
                </option>
                {range(0, numberOfOptions).map((index) => (
                  <option key={index}>{startQuantity + index * mustBuy}</option>
                ))}
                <option value="other">Other</option>
              </select>
              <label className="form__label">
                {itemQuantityText}
                <span className="label__required"> - Required</span>
              </label>
              <i className="icon-down-micro form__control-icon form__control-icon--lg" />
            </div>
          </div>
        )}
        <div className="form__group">
          {showCustomQuantity && (
            <>
              <label className="form__label">
                {itemQuantityText}
                <span className="label__required"> - Required</span>
              </label>
              <input
                data-testid="quantityInputForVG"
                autoComplete="off"
                type="number"
                className={`form__control ${errors.length > 0 ? "error" : ""}`}
                name="qty"
                defaultValue={quantity}
                aria-label="Quantity"
                onChange={handleOnChange}
              />
            </>
          )}
        </div>
      </div>
      <div className="add-to-cart">
        {errors &&
          errors.map((error) => (
            <p key={error} className="messages" data-testid="qtyInputError">
              {error}
            </p>
          ))}
      </div>
    </div>
  );
};

export default VirtualGroupingQuantitySelection;
